import React, { useMemo, useState } from 'react';
import { AlternativeRankService } from '../../../services/AlternativeRankService';
import type { AlternativeRank } from '../../../types/alternativeRanks';

interface AlternativeCareerAdvancesTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRulebookIds: string[];
}

export const AlternativeCareerAdvancesTab: React.FC<AlternativeCareerAdvancesTabProps> = ({
  searchTerm,
  setSearchTerm,
  selectedRulebookIds
}) => {
  const [expandedRanks, setExpandedRanks] = useState<Record<string, boolean>>({});
  const [selectedCareerFilter, setSelectedCareerFilter] = useState<string>('all');

  const allAlternativeRanks = useMemo(
    () =>
      AlternativeRankService.getAllAlternativeRanks().sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
    []
  );

  const availableCareers = useMemo(
    () =>
      Array.from(new Set(allAlternativeRanks.map((rank) => rank.career))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [allAlternativeRanks]
  );

  const filteredRanks = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return allAlternativeRanks.filter((rank) => {
      const matchesRulebook = selectedRulebookIds.includes(rank.rulebook);
      const matchesCareer = selectedCareerFilter === 'all' || rank.career === selectedCareerFilter;
      const matchesSearch =
        normalizedSearch === '' ||
        rank.name.toLowerCase().includes(normalizedSearch) ||
        rank.description.toLowerCase().includes(normalizedSearch) ||
        rank.career.toLowerCase().includes(normalizedSearch) ||
        rank.prerequisites.some((prereq) => prereq.name.toLowerCase().includes(normalizedSearch)) ||
        rank.advancements.some((advancement) =>
          advancement.name.toLowerCase().includes(normalizedSearch)
        );

      return matchesRulebook && matchesCareer && matchesSearch;
    });
  }, [allAlternativeRanks, searchTerm, selectedCareerFilter, selectedRulebookIds]);

  const toggleRank = (rankId: string) => {
    setExpandedRanks((current) => ({ ...current, [rankId]: !current[rankId] }));
  };

  const expandAll = () => {
    const nextExpanded: Record<string, boolean> = {};
    filteredRanks.forEach((rank) => {
      nextExpanded[rank.id] = true;
    });
    setExpandedRanks(nextExpanded);
  };

  const collapseAll = () => {
    setExpandedRanks({});
  };

  const getAdvancementCounts = (rank: AlternativeRank) => {
    const skills = rank.advancements.filter(
      (advancement) => advancement.category === 'skill'
    ).length;
    const talents = rank.advancements.filter(
      (advancement) => advancement.category === 'talent'
    ).length;

    return {
      total: rank.advancements.length,
      skills,
      talents
    };
  };

  return (
    <div>
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-ink mb-1">
              Search Alternative Ranks
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by rank, career, prerequisite, or advancement..."
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-ink mb-1">Career Filter</label>
            <select
              value={selectedCareerFilter}
              onChange={(e) => setSelectedCareerFilter(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Careers</option>
              {availableCareers.map((career) => (
                <option key={career} value={career}>
                  {career}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-2 text-sm bg-surface-active hover:bg-surface-active text-white rounded-md transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
        <div className="mt-2 text-sm text-ink-dim">
          Showing {filteredRanks.length} of {allAlternativeRanks.length} alternative ranks
        </div>
      </div>

      <div className="space-y-4">
        {filteredRanks.map((rank) => {
          const isExpanded = expandedRanks[rank.id];
          const counts = getAdvancementCounts(rank);

          return (
            <div key={rank.id} className="bg-surface border border-line rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleRank(rank.id)}
                className="w-full px-4 py-4 text-left hover:bg-surface-hover transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-ink">{rank.name}</h3>
                      <span className="px-2 py-1 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200">
                        {rank.career}
                      </span>
                      <span className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                        Rank {rank.minimumRank}+
                      </span>
                    </div>
                    <p className="text-sm text-ink-dim mb-2">{rank.description}</p>
                    <div className="text-xs text-ink-faint">
                      {counts.total} advancements, {counts.skills} skills, {counts.talents} talents
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-ink-faint transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-page/40 rounded-lg p-3">
                      <h4 className="font-medium text-ink mb-2">Prerequisites</h4>
                      {rank.prerequisites.length > 0 ? (
                        <ul className="space-y-1 text-sm text-ink">
                          {rank.prerequisites.map((prerequisite, index) => (
                            <li key={`${rank.id}-prereq-${index}`}>
                              {prerequisite.type}: {prerequisite.name}
                              {prerequisite.value !== undefined ? ` ${prerequisite.value}` : ''}
                              {prerequisite.training ? ` (${prerequisite.training})` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-ink-faint">No prerequisites</p>
                      )}
                    </div>

                    <div className="bg-page/40 rounded-lg p-3">
                      <h4 className="font-medium text-ink mb-2">Granted On Selection</h4>
                      <div className="space-y-2 text-sm text-ink">
                        <div>
                          <span className="font-medium">Talents:</span>{' '}
                          {rank.grantedTalents && rank.grantedTalents.length > 0
                            ? rank.grantedTalents.join(', ')
                            : 'None'}
                        </div>
                        <div>
                          <span className="font-medium">Traits:</span>{' '}
                          {rank.grantedTraits && rank.grantedTraits.length > 0
                            ? rank.grantedTraits.join(', ')
                            : 'None'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-surface-alt">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-ink">Type</th>
                          <th className="px-3 py-2 text-left font-medium text-ink">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-ink">
                            Prerequisites
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-ink">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {rank.advancements.map((advancement) => (
                          <tr key={advancement.id} className="hover:bg-surface-hover">
                            <td className="px-3 py-2">
                              <span
                                className={`inline-block px-2 py-1 text-xs rounded ${
                                  advancement.category === 'skill'
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200'
                                }`}
                              >
                                {advancement.category}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-medium text-ink">{advancement.name}</td>
                            <td className="px-3 py-2 text-ink-dim">
                              {advancement.prerequisites || '—'}
                            </td>
                            <td className="px-3 py-2 text-ink">{advancement.cost} XP</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredRanks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No alternative ranks found matching your filters</p>
        </div>
      )}
    </div>
  );
};
